import { useMemo } from 'react';

import { type Board } from '@air/api/types';
import {
  BoardAddIcon,
  BoardDuplicateIcon,
  BoardsIcon,
  DownloadIcon,
  FinderIcon,
  LinkIcon,
  Maximize2Icon,
  MousePointerClickIcon,
  MoveBoardIcon,
  PencilIcon,
  SlackIcon,
  SparklesIcon,
  StarFilledIcon,
  StarIcon,
  Trash2Icon,
} from '@air/design-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useBreakpointsContext } from '@air/provider-media-query';
import { useAirModal } from '@air/provider-modal';

import { displaySlackChannelName } from '~/components/EditPrivateBoardModal/EditBoardModal/SlackIntegration/utils/displaySlackChannelName';
import { useEditBoard } from '~/components/EditPrivateBoardModal/hooks/useEditBoard';
import { useDeleteBoardsModal } from '~/components/Modals/DeleteBoards/useDeleteBoardsModal';
import { useDuplicateBoard } from '~/components/Modals/DuplicateBoard/hooks/useDuplicateBoard';
import { useMoveItemsModal } from '~/components/Modals/MoveItemsModal/useMoveItemsModal';
import { SharePrivateBoardModal } from '~/components/SharePrivateBoardModal/SharePrivateBoardModal';
import { useLaunchBoardTemplatesInWorkspaceAgent } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useLaunchBoardTemplatesInWorkspaceAgent';
import { usePrivateAddAllBoardAssetsToCanvas } from '~/hooks/canvases/usePrivateAddAllBoardAssetsToCanvas';
import { useExcludedIntegrationsFlag } from '~/hooks/flags/useExcludedIntegrationsFlag';
import { useBoardPermissions } from '~/hooks/useBoardPermissions';
import { useCanShowInFinderOption } from '~/hooks/useCanShowInFinderOption';
import { useCreateSubBoard } from '~/hooks/useCreateSubBoard';
import { useDownloadPrivateBoard } from '~/hooks/useDownloadPrivateBoard';
import { useGoToPrivateBoardPage } from '~/hooks/useGoToPrivateBoardPage';
import { useDesktopAppConnectionContext } from '~/providers/DesktopAppConnectionProvider';
import { useGetBoardContainerPermissions } from '~/swr-hooks/boards/useGetBoardContainerPermissions';
import { useToggleFavoritedBoard } from '~/swr-hooks/boards/useToggleFavoritedBoard';
import { useSlackMenuOptions } from '~/utils/menuOptions/useSlackMenuOptions';
import {
  canChangeBoardDetails,
  canCreateBoard,
  canCreateCanvasInBoard,
  canDeleteBoard,
  canDuplicateBoard,
} from '~/utils/permissions/boardPermissions';
import { canViewShortUrl } from '~/utils/permissions/shortUrlPermissions';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';

const TRACK_LOCATION = 'side-nav-tree-item';

export type UseWorkspaceSidebarBoardDropdownOptionsParams = {
  board: Board;
  open: boolean | undefined;
  onRename: () => void;
};

export const useWorkspaceSidebarBoardDropdownOptions = ({
  board,
  open,
  onRename,
}: UseWorkspaceSidebarBoardDropdownOptionsParams) => {
  const { getBoardContainerPermissions } = useGetBoardContainerPermissions();
  const { boardPermissions } = useBoardPermissions({
    boardId: board.id,
  });
  const { goToBoardPage } = useGoToPrivateBoardPage();
  const { downloadPrivateBoard } = useDownloadPrivateBoard();
  const { canShowOpenInFinder } = useCanShowInFinderOption();
  const { openBoardInFinder } = useDesktopAppConnectionContext();
  const { createSubBoard } = useCreateSubBoard();
  const { toggleFavoritedBoard } = useToggleFavoritedBoard();
  const { showDuplicateBoardsModal } = useDuplicateBoard();
  const { showEditBoardModal } = useEditBoard();
  const { showMoveItemsModal } = useMoveItemsModal();
  const { onConnectToSlack, onConnectedToSlack, slackIntegrationData } = useSlackMenuOptions(open ? board : undefined);
  const { excludedIntegrations } = useExcludedIntegrationsFlag();
  const { showDeleteBoardsModal } = useDeleteBoardsModal();
  const [showSharePrivateBoardModal] = useAirModal(SharePrivateBoardModal);
  const { canLaunchBoardTemplatesInAgent, launchBoardTemplatesInAgent } = useLaunchBoardTemplatesInWorkspaceAgent();
  const { addAllBoardAssetsToCanvas } = usePrivateAddAllBoardAssetsToCanvas();
  const { isAboveMediumScreen } = useBreakpointsContext();

  const boardContainerPermissions = getBoardContainerPermissions({ board });

  const workspaceSidebarBoardDropdownOptions = useMemo(() => {
    const workspaceSidebarBoardDropdownOptionsArray: DropdownMenuOption[] = [];

    workspaceSidebarBoardDropdownOptionsArray.push({
      id: 'open',
      label: 'Open',
      onSelect: () => goToBoardPage({ board }),
      prefix: <Maximize2Icon size="sm" />,
      type: 'item',
    });

    if (canGenerateZip(boardPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'download',
        label: 'Download',
        onSelect: () =>
          downloadPrivateBoard({
            boardId: board.id,
            trackLocation: TRACK_LOCATION,
          }),
        prefix: <DownloadIcon size="sm" />,
        type: 'item',
      });
    }

    if (canShowOpenInFinder && canGenerateZip(boardPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'open-in-finder',
        label: 'Open in Finder',
        onSelect: () => openBoardInFinder(board.id),
        prefix: <FinderIcon size="sm" />,
        type: 'item',
      });
    }

    workspaceSidebarBoardDropdownOptionsArray.push({
      id: 'group-1-separator',
      type: 'separator',
    });

    if (canViewShortUrl(boardPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'share-link',
        label: 'Share link',
        onSelect: () => showSharePrivateBoardModal({ boardId: board.id, trackLocation: TRACK_LOCATION }),
        prefix: <LinkIcon size="sm" />,
        type: 'item',
      });
    }

    if (isAboveMediumScreen && canCreateCanvasInBoard(boardPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'add-all-to-canvas',
        label: 'Add all to Canvas\u2026',
        onSelect: () => addAllBoardAssetsToCanvas(board),
        prefix: <MousePointerClickIcon size="sm" />,
        type: 'item',
      });
    }

    if (canCreateBoard(boardPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'add-sub-board',
        label: 'Add sub-board',
        onSelect: () =>
          createSubBoard({
            selectedParentBoard: board,
            trackLocation: TRACK_LOCATION,
          }),
        prefix: <BoardAddIcon size="sm" />,
        type: 'item',
      });
    }

    if (canCreateBoard(boardPermissions) && canLaunchBoardTemplatesInAgent) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'generate-board-structure',
        label: 'Board templates',
        onSelect: () => launchBoardTemplatesInAgent(board),
        prefix: <SparklesIcon size="sm" />,
        type: 'item',
      });
    }

    if (!board.hasCurrentUser) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'favorite',
        label: 'Favorite',
        onSelect: () => toggleFavoritedBoard({ board, trackLocation: TRACK_LOCATION }),
        prefix: <StarIcon size="sm" />,
        type: 'item',
      });
    } else {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'unfavorite',
        label: 'Unfavorite',
        onSelect: () => toggleFavoritedBoard({ board, trackLocation: TRACK_LOCATION }),
        prefix: <StarFilledIcon size="sm" />,
        type: 'item',
      });
    }

    workspaceSidebarBoardDropdownOptionsArray.push({
      id: 'group-2-separator',
      type: 'separator',
    });

    if (canChangeBoardDetails(boardPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'rename',
        label: 'Rename',
        onSelect: onRename,
        prefix: <PencilIcon size="sm" />,
        type: 'item',
      });
    }

    if (
      canDuplicateBoard({
        boardContext: boardPermissions,
        targetContext: boardContainerPermissions,
      })
    ) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'duplicate',
        label: 'Duplicate',
        onSelect: () => showDuplicateBoardsModal({ boards: [board], trackLocation: TRACK_LOCATION }),
        prefix: <BoardDuplicateIcon size="sm" />,
        type: 'item',
      });
    }

    if (canChangeBoardDetails(boardPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'edit-board-details',
        label: 'Edit board details',
        onSelect: () => showEditBoardModal({ board, trackLocation: TRACK_LOCATION }),
        prefix: <BoardsIcon size="sm" />,
        type: 'item',
      });
    }

    if (canDeleteBoard(boardContainerPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'move-to',
        label: 'Move to…',
        onSelect: () => showMoveItemsModal({ boards: [board], trackLocation: TRACK_LOCATION }),
        prefix: <MoveBoardIcon size="sm" />,
        type: 'item',
      });
    }

    if (onConnectToSlack || onConnectedToSlack) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'group-3-separator',
        type: 'separator',
      });

      if (onConnectToSlack && !excludedIntegrations.includes('slack')) {
        workspaceSidebarBoardDropdownOptionsArray.push({
          id: 'connect-to-slack',
          label: 'Connect to Slack',
          onSelect: () => onConnectToSlack(TRACK_LOCATION),
          prefix: <SlackIcon size="sm" />,
          type: 'item',
        });
      }

      if (onConnectedToSlack && slackIntegrationData) {
        workspaceSidebarBoardDropdownOptionsArray.push({
          id: 'connected-to-slack',
          label: 'Connected to Slack',
          description: displaySlackChannelName(slackIntegrationData.channelName),
          onSelect: () => onConnectedToSlack(TRACK_LOCATION),
          prefix: <SlackIcon size="sm" />,
          type: 'item',
        });
      }
    }

    if (canDeleteBoard(boardContainerPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'group-4-separator',
        type: 'separator',
      });

      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'delete',
        className: 'ads:text-error text-red-9',
        label: 'Delete from workspace',
        onSelect: () => showDeleteBoardsModal({ boardIds: [board.id], trackLocation: TRACK_LOCATION }),
        prefix: <Trash2Icon size="sm" />,
        type: 'item',
      });
    }

    return workspaceSidebarBoardDropdownOptionsArray;
  }, [
    addAllBoardAssetsToCanvas,
    board,
    isAboveMediumScreen,
    boardContainerPermissions,
    boardPermissions,
    canShowOpenInFinder,
    createSubBoard,
    downloadPrivateBoard,
    excludedIntegrations,
    goToBoardPage,
    onConnectToSlack,
    onConnectedToSlack,
    onRename,
    openBoardInFinder,
    showDeleteBoardsModal,
    showDuplicateBoardsModal,
    showEditBoardModal,
    showMoveItemsModal,
    showSharePrivateBoardModal,
    slackIntegrationData,
    toggleFavoritedBoard,
    canLaunchBoardTemplatesInAgent,
    launchBoardTemplatesInAgent,
  ]);

  return {
    workspaceSidebarBoardDropdownOptions,
  };
};
